import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { fileToUploadDataUrl } from '../lib/documents.js';
import { useAuth } from '../context/AuthContext.jsx';
import Button from './Button.jsx';
import Card from './Card.jsx';
import { addressLineError, nameError } from '@hfb/shared';
import Input, { Select, Textarea } from './Input.jsx';

const VEHICLES = [
  ['bike', 'Motorbike'],
  ['scooter', 'Scooter'],
  ['ev', 'Electric scooter'],
  ['cycle', 'Bicycle'],
];

const PROOF_TYPES = [
  ['electricity_bill', 'Electricity bill'],
  ['water_bill', 'Water bill'],
  ['gas_bill', 'Gas bill'],
  ['rent_agreement', 'Rent agreement'],
  ['voter_id', 'Voter ID'],
  ['passport', 'Passport'],
  ['bank_statement', 'Bank statement'],
  ['bank_passbook', 'Bank passbook'],
  ['driving_licence', 'Driving licence'],
];

const DOCUMENTS = [
  { type: 'aadhaar_front', label: 'Aadhaar card, front', hint: 'The side with your photo and name.' },
  { type: 'aadhaar_back', label: 'Aadhaar card, back', hint: 'The side with your address.' },
  { type: 'address_proof', label: 'Address proof', hint: 'A photo or PDF of the document you chose above.' },
];
export const DOCUMENT_LABEL = Object.fromEntries(DOCUMENTS.map((d) => [d.type, d.label]));

const errText = (err, fallback) => err?.data?.error ?? err?.message ?? fallback;

function Shell({ children }) {
  return <div className="mx-auto max-w-lg px-4 pb-24 pt-8">{children}</div>;
}

/** Shared with DriverRoute (same key), so this reads from its cache. */
function usePartnerMe() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['partner-me', user?.id],
    queryFn: () => api.get('/api/partners/me'),
    enabled: Boolean(user),
  });
}

/** One document slot: pick a photo or PDF, it uploads straight away. */
function DocumentUpload({ doc, uploaded, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      const dataUrl = await fileToUploadDataUrl(file);
      await api.post('/api/partners/documents', { docType: doc.type, dataUrl });
      onUploaded();
    } catch (err) {
      setError(errText(err, 'Could not upload that file.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`rounded-2xl border p-3 ${uploaded ? 'border-leaf-600 bg-leaf-100/60' : 'border-oat-dark bg-surface'}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-heading">
            {uploaded ? <span aria-hidden="true">✓ </span> : null}
            {doc.label}
          </p>
          <p className="mt-0.5 text-xs text-ink-muted">{uploaded ? 'Uploaded' : doc.hint}</p>
        </div>
        <label
          className={`inline-flex min-h-touch shrink-0 cursor-pointer items-center rounded-full border border-leaf-600 px-4 text-sm font-semibold text-brand ${busy ? 'pointer-events-none opacity-60' : ''}`}
        >
          {busy ? 'Uploading…' : uploaded ? 'Replace' : 'Upload'}
          {/* No `capture`: that forces the camera and hides the gallery and PDFs. */}
          <input
            type="file"
            accept="image/*,application/pdf"
            className="sr-only"
            disabled={busy}
            onChange={(e) => {
              upload(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </label>
      </div>
      {error ? <p role="alert" className="mt-2 text-xs text-persimmon-dark">{error}</p> : null}
    </div>
  );
}

/**
 * Apply to become an on-demand delivery partner: details, vehicle, and the
 * identity documents an admin reviews before approving. Submitting makes the
 * account a *pending* partner and swaps in a fresh session, since the role
 * lives in the access token.
 */
export default function PartnerApply({ rejected = null }) {
  const { user, adoptSession, logout } = useAuth();
  const qc = useQueryClient();
  const meQ = usePartnerMe();
  const initialName = user?.fullName && user.fullName !== 'Friend' ? user.fullName : '';
  // Set on the first submit attempt: errors stay hidden while they're typing.
  const [tried, setTried] = useState(false);
  const [form, setForm] = useState({
    fullName: initialName,
    vehicleType: rejected?.vehicleType ?? 'bike',
    vehicleNumber: rejected?.vehicleNumber ?? '',
    licenceNumber: rejected?.licenceNumber ?? '',
    aadhaarLast4: rejected?.aadhaarLast4 ?? '',
    residentialAddress: rejected?.residentialAddress ?? '',
    addressProofType: rejected?.addressProofType ?? 'electricity_bill',
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadedTypes = new Set((meQ.data?.documents ?? []).map((d) => d.docType));
  const allUploaded = DOCUMENTS.every((d) => uploadedTypes.has(d.type));
  const refreshDocs = () => qc.invalidateQueries({ queryKey: ['partner-me'] });

  const apply = useMutation({
    mutationFn: () => api.post('/api/partners/apply', {
      fullName: form.fullName.trim(),
      vehicleType: form.vehicleType,
      vehicleNumber: form.vehicleNumber.trim() || undefined,
      licenceNumber: form.licenceNumber.trim(),
      aadhaarLast4: form.aadhaarLast4,
      residentialAddress: form.residentialAddress.trim(),
      addressProofType: form.addressProofType,
    }),
    onSuccess: async (data) => {
      await adoptSession(data);
      refreshDocs();
    },
    onError: refreshDocs,
  });

  // Shown against the field once they've tried to submit, so nobody is left
  // staring at a dead button wondering which line is wrong.
  const errors = {
    fullName: nameError(form.fullName),
    aadhaarLast4: /^\d{4}$/.test(form.aadhaarLast4) ? null : 'Enter the last 4 digits of your Aadhaar',
    residentialAddress: addressLineError(form.residentialAddress, { label: 'address', min: 10, max: 300 }),
    licenceNumber: form.licenceNumber.trim().length >= 5 ? null : 'Enter your driving licence number',
  };
  const detailsValid = !Object.values(errors).some(Boolean);
  const hasHistory = apply.error?.data?.code === 'HAS_CUSTOMER_HISTORY';

  return (
    <Shell>
      <span className="eyebrow">Delivery partner</span>
      <h1 className="mt-2 font-display text-3xl text-heading">
        {rejected ? 'Apply again' : 'Deliver with HealthyFruitsBox'}
      </h1>
      <p className="mt-2 text-sm text-ink-muted">
        Go online when it suits you, accept deliveries near our outlet, and earn for every box
        plus extra for longer trips.
      </p>

      {rejected?.rejectionReason ? (
        <Card tone="quiet" className="mt-5">
          <p className="text-sm font-semibold text-heading">Your last application wasn’t approved</p>
          <p className="mt-1 text-sm text-ink-muted">{rejected.rejectionReason}</p>
        </Card>
      ) : null}

      <h2 className="mt-7 font-display text-lg text-heading">1. About you</h2>
      <div className="mt-3 space-y-4">
        <Input label="Full name (as on Aadhaar)" value={form.fullName} onChange={set('fullName')} autoComplete="name"
          maxLength={120} error={tried ? errors.fullName : undefined} />
        <Input
          label="Last 4 digits of your Aadhaar"
          value={form.aadhaarLast4}
          onChange={(e) => setForm((f) => ({ ...f, aadhaarLast4: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
          inputMode="numeric"
          maxLength={4}
          placeholder="1234"
          error={tried ? errors.aadhaarLast4 : undefined}
          hint={tried && errors.aadhaarLast4 ? undefined : 'We never ask for or store your full Aadhaar number.'}
        />
        <Textarea
          label="Home address"
          value={form.residentialAddress}
          onChange={set('residentialAddress')}
          rows={3}
          maxLength={300}
          placeholder="House no., street, area, city, PIN"
          error={tried ? errors.residentialAddress : undefined}
        />
      </div>

      <h2 className="mt-7 font-display text-lg text-heading">2. Your vehicle</h2>
      <div className="mt-3 space-y-4">
        <Select label="Vehicle" value={form.vehicleType} onChange={set('vehicleType')}>
          {VEHICLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </Select>
        {form.vehicleType !== 'cycle' ? (
          <Input label="Vehicle number" value={form.vehicleNumber} onChange={set('vehicleNumber')}
            placeholder="UP78 AB 1234" hint="Optional for now, but speeds up approval." />
        ) : null}
        <Input label="Driving licence number" value={form.licenceNumber} onChange={set('licenceNumber')}
          placeholder="UP7820190012345" autoCapitalize="characters" maxLength={30}
          error={tried ? errors.licenceNumber : undefined} />
      </div>

      <h2 className="mt-7 font-display text-lg text-heading">3. Documents</h2>
      <p className="mt-1 text-xs text-ink-muted">
        Clear, well-lit photos, all four corners visible. They’re stored privately and only our admin
        team can view them.
      </p>
      <div className="mt-3 space-y-3">
        <Select label="Address proof type" value={form.addressProofType} onChange={set('addressProofType')}>
          {PROOF_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </Select>
        {DOCUMENTS.map((doc) => (
          <DocumentUpload key={doc.type} doc={doc} uploaded={uploadedTypes.has(doc.type)} onUploaded={refreshDocs} />
        ))}
      </div>

      <div className="mt-7 space-y-3">
        {apply.isError ? (
          <p role="alert" className="rounded-2xl bg-persimmon-light px-4 py-2.5 text-sm text-persimmon-dark">
            {errText(apply.error, 'Could not submit your application.')}
            {hasHistory ? (
              <button type="button" onClick={logout} className="mt-1 block font-semibold underline">
                Sign out and use another number
              </button>
            ) : null}
          </p>
        ) : null}
        {!allUploaded ? (
          <p className="text-center text-xs text-ink-muted">
            Upload all three documents to submit.
          </p>
        ) : null}
        {tried && !detailsValid && (
          <p role="alert" className="text-center text-sm font-medium text-persimmon-dark">
            Something is missing. Please check the details above.
          </p>
        )}
        <Button
          className="w-full"
          loading={apply.isPending}
          disabled={!allUploaded}
          onClick={() => { setTried(true); if (detailsValid) apply.mutate(); }}
        >
          Submit application
        </Button>
        <button type="button" onClick={logout}
          className="block w-full min-h-touch text-center text-sm font-semibold text-ink-muted">
          Sign out
        </button>
      </div>
    </Shell>
  );
}

/** Where an application stands when there's nothing for the partner to do yet. */
export function PartnerStatus({ profile }) {
  const { logout, user } = useAuth();
  const meQ = usePartnerMe();
  const qc = useQueryClient();
  const uploadedTypes = new Set((meQ.data?.documents ?? []).map((d) => d.docType));
  const suspended = profile?.status === 'suspended';
  return (
    <Shell>
      <span className="eyebrow">Delivery partner</span>
      <h1 className="mt-2 font-display text-3xl text-heading">
        {suspended ? 'Account suspended' : 'Application under review'}
      </h1>
      <Card className="mt-5">
        {suspended ? (
          <>
            <p className="text-sm text-ink">You can’t go online right now.</p>
            {profile?.rejectionReason ? (
              <p className="mt-2 text-sm text-ink-muted">Reason: {profile.rejectionReason}</p>
            ) : null}
            <p className="mt-2 text-sm text-ink-muted">Contact the store if you think this is a mistake.</p>
          </>
        ) : (
          <>
            <p className="text-sm text-ink">
              Thanks for applying. We’re checking your documents and will send you a notification as
              soon as you are approved. Then you can go online and start taking deliveries.
            </p>
            <dl className="mt-4 grid grid-cols-2 gap-2 text-sm">
              <dt className="text-ink-muted">Vehicle</dt>
              <dd className="text-heading">{profile?.vehicleType ?? '—'}{profile?.vehicleNumber ? ` · ${profile.vehicleNumber}` : ''}</dd>
              <dt className="text-ink-muted">Licence</dt>
              <dd className="text-heading">{profile?.licenceNumber ?? '—'}</dd>
              <dt className="text-ink-muted">Aadhaar</dt>
              <dd className="text-heading">{profile?.aadhaarLast4 ? `XXXX XXXX ${profile.aadhaarLast4}` : '—'}</dd>
            </dl>
            <ul className="mt-4 space-y-1 text-sm">
              {DOCUMENTS.map((d) => (
                <li key={d.type} className={uploadedTypes.has(d.type) ? 'text-brand' : 'text-ink-muted'}>
                  {uploadedTypes.has(d.type) ? '✓' : '○'} {d.label}
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>
      {/* Which account this is and what the server last said — so a phone
          signed in to a different account than the one approved is obvious. */}
      <p className="mt-5 text-center text-xs text-ink-muted">
        Signed in as <span className="font-semibold text-heading">{user?.phone || user?.email || 'this account'}</span>
        {' · '}status: <span className="font-semibold text-heading">{profile?.status ?? 'unknown'}</span>
        {meQ.dataUpdatedAt ? ` · checked ${new Date(meQ.dataUpdatedAt).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', second: '2-digit' })}` : ''}
      </p>
      {/* Approval is picked up automatically, but let them check on demand too. */}
      <Button
        className="mt-5"
        variant="secondary"
        fullWidth
        loading={meQ.isFetching}
        onClick={() => qc.invalidateQueries({ queryKey: ['partner-me'] })}
      >
        Check status
      </Button>
      <button type="button" onClick={logout}
        className="mt-3 block w-full min-h-touch text-center text-sm font-semibold text-ink-muted">
        Sign out
      </button>
    </Shell>
  );
}
